import {useState, SetStateAction, useEffect} from 'react';
import {
  Drawer,
  TextField,
  Stack,
  Divider,
  Box,
  Grid,
  Tab,
  Tabs,
  Button,
  Alert,
  Snackbar
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useForm, FormProvider, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import DialogToolBar from './AdditionData/DialogToolBar';
import {CustomFile, UploadProps} from 'src/minimal-components/upload';
import {RHFCheckbox, RHFSingleUpload} from 'src/minimal-components/hook-form';
import Resources from './AdditionData/Resources';
import NewFilesLockProperties from './AdditionData/NewFilesLockProperties';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useAppContext} from '@context/AppContext';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {enqueueSnackbar} from 'notistack';
import EncryptUtils from 'src/utils/EncryptUtils';
import LoadingButton from '@mui/lab/LoadingButton';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import {BASE_RECORD_ID, LMS_IS_ADMIN} from 'src/routes/BaseRoute';
import {useModulePreviewContext} from '@context/ModulePreviewContextProvider';
interface ModuleData {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  type: string;
  sequence: number;
  module_lock_type_id?: number;
  locked_until_date?: string;
  Module_Completed?: boolean;
  IsUnlocked?: boolean;
  is_completed?: boolean;
  locked_linked_items?: {
    id: string;
    item_name: string;
    entity_completion_sub_type_id: number;
    module_link_id: number;
    module_id: number;
    type: 'file' | 'link' | 'content' | 'assignment' | 'onlinetest' | 'module';
  }[];
  files?: {
    id: string;
    name: string;
    ParentName: string;
    blob_file_extension?: string;
    fileexpanded: boolean;
    time?: string;
    score?: string;
    lock_type_id: number;
    locked_until_date?: string;
    type: string;
    module_id: number;
    sequence: number;
    ChildItems_Completed?: boolean;
    IsUnlocked?: boolean;
    is_completed?: boolean;
    additionalFiles?: {id: string; name: string}[];
    locked_linked_items?: {
      id: string;
      item_name: string;
      entity_completion_sub_type_id: number;
      module_link_id: number;
      module_id: number;
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
    }[];
  }[];
}
const StyledLabel = styled('span')(({theme}) => ({
  ...theme.typography.caption,
  width: 100,
  flexShrink: 0,
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightSemiBold
}));
const isValidEmbedURL = (value: string): boolean => {
  // Regex to extract the `src` attribute if it's an iframe
  const iframeSrcMatch = value.match(/<iframe.*?src="(.*?)"/);

  if (iframeSrcMatch) {
    // Validate the extracted `src` URL
    const srcUrl = iframeSrcMatch[1];
    try {
      new URL(srcUrl); // Check if it's a valid URL
      return true;
    } catch {
      return false;
    }
  }

  // If it's not an iframe, validate it as a plain URL
  try {
    new URL(value); // Check if it's a valid URL
    return true;
  } catch {
    return false;
  }
};
// Validation schema using yup
const validationSchema = yup.object().shape({
  taskName: yup.string().required('Task name is required'),
  EmbedURL: yup
    .string()
    .required('Embed URL is required')
    .test(
      'is-valid-embed-url',
      'Embed URL must be a valid URL or an iframe with a valid src',
      value => (value ? isValidEmbedURL(value) : false)
    ),
  taskDescription: yup.string().notRequired(),
  MarkFileAsMandatory: yup.boolean().nullable().notRequired()
});

interface IKanbanTask {
  id: string;
  name: string;
  EmbedURL: string;
  description?: string | null;
  MarkFileAsMandatory?: boolean;
}
interface Propsnew extends Omit<UploadProps, 'file'> {
  multiple?: boolean;
}

interface Props extends Propsnew {
  task: any;
  uiFieldsdata: any;
  // eslint-disable-next-line react/boolean-prop-naming
  taskDone: any;
  openDetails: boolean;
  onCloseDetails: VoidFunction;
  onUpdateTask?: (updateTask: IKanbanTask) => void;
}

export default function AddFileSModule({
  task,
  uiFieldsdata,
  openDetails,
  onCloseDetails,
  onUpdateTask,
  taskDone,
  ...other
}: Props) {
  const {
    appState: {applicationName}
  } = useAppContext();
  const lmsisAdmin = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_IS_ADMIN
  );
  const {ModulePreviewData, SetModulePreviewData} = useModulePreviewContext();
  const [tabActive, setTabsActive] = useState<boolean>(true);
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      taskName: task?.data?.name || '',
      EmbedURL: task?.data?.EmbedURL || '',
      taskDescription: task?.data?.description || '',
      MarkFileAsMandatory: task?.data?.MarkFileAsMandatory || true
    }
  });
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const [isloading, setIsloading] = useState(false);
  const [uiFields, setUiFields] = useState<any>(uiFieldsdata);
  const [isloaded, setIsLoaded] = useState(false);
  const [insertedId, setInsertedId] = useState<any>();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [saved, setSaved] = useState<boolean>(false);
  const findFileInModule = (
    data: any[],
    moduleId: number,
    fileId: number,
    type: string
  ) => {
    const module = data.find(m => m.id == moduleId);
    if (!module) return null;
    const file = module.files?.find(
      (f: {id: number; type: string}) => f.id == fileId && f.type === type
    );
    return file ?? null;
  };
  const handleChange = (event: any, newValue: SetStateAction<number>) => {
    if (tabActive) {
      setOpenSnackbar(true); // Show the snackbar when `tabActive` is true
    } else {
      setActiveTab(newValue); // Switch tabs
    }
  };
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
    setSaved(false);
  };
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {errors, isSubmitting}
  } = methods;

  const values = watch();
  const init = async () => {
    try {
      setIsloading(true);
      let res;
      if (uiFieldsdata === undefined) {
        res = await APIService.getSearchForm(
          applicationName,
          'module',
          'AddLinks'
        );
        setUiFields(res.searchForm);
      } else {
        setUiFields(uiFieldsdata);
      }
      const fileData = findFileInModule(
        ModulePreviewData,
        Number(insertedId?.data[0].module_id),
        Number(insertedId?.data[0].inserted_id),
        insertedId?.data[0].type
      );
      if (fileData) {
        setValue('taskName', fileData?.disable_seek);
        setValue('EmbedURL', fileData?.enable_file_download);
        setValue('taskDescription', fileData?.mark_as_introductory_file);
        setValue('MarkFileAsMandatory', fileData?.time);
      } else {
        if (
          (res?.searchForm || uiFieldsdata !== undefined) &&
          (insertedId || task?.inserted_id)
        ) {
          const {sql_data} =
            uiFieldsdata['create_sql'] || res.searchForm['create_sql'];
          let operations;
          if (insertedId || task?.inserted_id) {
            operations = 'SEARCH';
          }
          const data: any = {
            sql_data: sql_data,
            data: {
              id: insertedId?.data[0]?.inserted_id || task?.inserted_id,
              course_overview_id: uniqueId,
              tableName: 'ModuleFileSubList'
            },
            operation: operations
          };
          const encryptRequestData = EncryptUtils.encryptedData(data);
          const result = await APIService.getLMSResultsData(
            applicationName,
            'LMS',
            {
              tk: encryptRequestData
            }
          );
          setValue('taskName', result.data[0]?.name || '');
          setValue('EmbedURL', result.data[0]?.embedded_url || '');
          setValue('taskDescription', result.data[0]?.description || '');
          setValue('MarkFileAsMandatory', result.data[0]?.mark_as_mandatory);
          setIsloading(false);
          // if (result.data && result.data.length > 0) {
          //   const updatedEntities = result.data.map(
          //     (resourceFile: {
          //       id: any;
          //       name: any;
          //       type: any;
          //       blob_url: any;
          //       size: any;
          //     }) => ({
          //       id: resourceFile.id ?? undefined, // Assign entity_id
          //       blobid: resourceFile.id ?? undefined, // Assign type_id
          //       name: resourceFile.name ?? '', // Assign sub_type_id
          //       type: resourceFile.type ?? '',
          //       blobUrl: resourceFile.blob_url ?? '',
          //       size: resourceFile.size ?? 0
          //     })
          //   );
          //   setUploadedFile(updatedEntities);
          //   const uploadfiles = result.data.map(
          //     (resourceFile: {
          //       id: string;
          //       name: string;
          //       type: string;
          //       blob_url: string;
          //       size: number;
          //     }): UploadFile => ({
          //       id: resourceFile.id ?? '', // Fallback to empty string if undefined
          //       file: {
          //         id: resourceFile.id ?? '', // Ensure it has an ID
          //         name: resourceFile.name ?? '', // Name of the file
          //         type: resourceFile.type ?? '', // File type
          //         size: resourceFile.size ?? 0, // File size in bytes
          //         lastModified: new Date().getTime(), // Mock last modified
          //         preview: resourceFile.blob_url ?? '', // Use blob URL as a preview
          //         path: undefined, // Default path to undefined
          //         slice: () => new Blob().slice(), // Mock implementation
          //         arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)), // Mock implementation
          //         stream: () => new Blob().stream(), // Mock implementation
          //         webkitRelativePath: '' // Default empty string
          //       } as CustomFile,
          //       preview: resourceFile.blob_url ?? '' // Set optional preview
          //     })
          //   );
          //   const displayprogressdata = result.data.map((res: {id: any}) => ({
          //     id: res.id ?? undefined,
          //     display: false,
          //     progress: 100
          //   }));
          //   // Update the form's entities field
          //   await setValue('bloBurls', updatedEntities, {shouldValidate: true});
          //   await setValue('uploadResourcesfiles', uploadfiles);
          //   await setValue('displayprogress', displayprogressdata);
          // }
          // setResourceLoder(false);
        }
      }
      setIsloading(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      setIsloading(false);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      if (task) {
        setTabsActive(false);
      }

      if (taskDone?.operation === 'INSERT') {
        setInsertedId(undefined);
      } else if (insertedId !== undefined) {
        setInsertedId(insertedId);
      } else {
        setInsertedId({
          data: [
            {
              module_id: task?.module_id,
              inserted_id: task?.inserted_id,
              type: 'link'
            }
          ]
        });
      }
      await init();
    };

    fetchData();
  }, [openDetails]);

  // Handle removing all files

  const onSubmit = async (datavalue: any) => {
    onUpdateTask?.({
      ...task,
      name: datavalue.taskName,
      EmbedURL: datavalue.EmbedURL,
      description: datavalue?.taskDescription
    });
    try {
      if (uiFields) {
        const {sql_data} = uiFields['create_sql'];
        let operations;
        if (taskDone?.id && taskDone?.operation) {
          operations = taskDone?.operation;
          datavalue.module_id = taskDone?.id;
        }
        if (insertedId && insertedId.data.length > 0) {
          operations = 'UPDATE';
          datavalue.id = insertedId.data[0].inserted_id;
          datavalue.page = 1;
        }
        datavalue.course_overview_id = uniqueId;
        datavalue.isAdmin = lmsisAdmin === 'True';
        // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
        setIsLoaded(true);
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await APIService.getLMSResultsData(
          applicationName,
          'LMS',
          {
            tk: encryptRequestData
          }
        );
        if (result.data[0]) {
          setInsertedId({data: [{...result.data[0], type: 'link'}]});
        }
        const processModulesData = (data: ModuleData[]) => {
          return data.map(module => ({
            ...module,
            files:
              module.files?.map(file => ({
                ...file,
                locked_linked_items:
                  typeof file.locked_linked_items === 'string'
                    ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
                    : file.locked_linked_items || [], // Keep array or default to empty
                additionalFiles:
                  typeof file.additionalFiles === 'string'
                    ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
                    : file.additionalFiles || [] // Keep array or default to empty
              })) || []
          }));
        };
        const syncExpandedStates = (
          transformed: ModuleData[],
          existing: ModuleData[]
        ) => {
          return transformed.map(module => {
            const matchingModule = existing.find(e => e.id === module.id);

            if (!matchingModule) return module;

            // Sync the `expanded` flag
            const updatedModule = {
              ...module,
              expanded: matchingModule.expanded || false,
              files:
                module?.files &&
                module?.files.map(file => {
                  const matchingFile =
                    matchingModule?.files &&
                    matchingModule.files.find(f => f.id === file.id);
                  return {
                    ...file,
                    fileexpanded: matchingFile?.fileexpanded || false
                  };
                })
            };

            return updatedModule;
          });
        };

        // Handling API result
        if (result.data[1]) {
          const transformData = await processModulesData(result.data[1]);
          const updatedTransformed = syncExpandedStates(
            transformData,
            ModulePreviewData
          );
          await SetModulePreviewData(updatedTransformed);
        }
        setIsLoaded(false);
        setSaved(true);
        setTabsActive(false);
        setActiveTab(1);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
    setTabsActive(false);
  };

  return (
    <FormProvider {...methods}>
      <Drawer
        open={openDetails}
        onClose={onCloseDetails}
        anchor="right"
        slotProps={{
          backdrop: {invisible: true}
        }}
        PaperProps={{
          sx: {
            width: {
              xs: 1,
              sm: '47vw'
            }
          }
        }}>
        <DialogToolBar
          title={task ? 'Edit Link' : 'Add Link'}
          onCloseDetails={onCloseDetails}
        />
        <Divider />
        <Box sx={{width: '100%'}}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}>
            <Grid item xs={12}>
              <Tabs
                value={activeTab}
                onChange={handleChange}
                variant="fullWidth"
                textColor="primary"
                sx={{
                  backgroundColor: '#F4F6F8',
                  '& .MuiTab-root': {
                    fontSize: '16px',
                    fontWeight: '500',
                    '&:hover': {
                      backgroundColor: '#e0e0e0', // Change background on hover
                      borderRadius: 2
                    }
                  },
                  '& .MuiTab-root.Mui-selected': {
                    color: 'primary.main',
                    margin: 1,
                    backgroundColor: 'white',
                    borderRadius: 2
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#637381',
                    height: 0
                  }
                }}>
                <Tab label="Basic Info" />
                <Tab label="Lock Properties" disabled={isloading || isloaded} />
                <Tab label="Resources" disabled={isloading || isloaded} />
              </Tabs>
            </Grid>
          </Grid>
        </Box>
        {activeTab === 0 &&
          (isloading ? (
            <Stack
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              minHeight={'50vh'}>
              <LoadingScreen />
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} style={{overflowY: 'auto'}}>
              <Stack spacing={1.5} sx={{pt: 3, pb: 5, px: 2.5}}>
                {/* Task Name */}
                <Stack spacing={2} direction="column">
                  <StyledLabel>Name</StyledLabel>
                  <Controller
                    name="taskName"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        error={!!errors.taskName}
                        helperText={errors.taskName?.message}
                      />
                    )}
                  />
                </Stack>
                <Stack spacing={2} direction="column">
                  <StyledLabel>Embed URL</StyledLabel>
                  <Controller
                    name="EmbedURL"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        error={!!errors.EmbedURL}
                        helperText={errors.EmbedURL?.message}
                      />
                    )}
                  />
                </Stack>

                <Stack spacing={2} direction="column">
                  <StyledLabel>Description</StyledLabel>
                  <Controller
                    name="taskDescription"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        size="small"
                        InputProps={{
                          sx: {
                            typography: 'body2',
                            minHeight: '12vh',
                            alignItems: 'baseline'
                          }
                        }}
                        sx={{minHeight: '12vh'}}
                        error={!!errors.taskDescription}
                        helperText={errors.taskDescription?.message}
                      />
                    )}
                  />
                </Stack>
                <Stack direction="row">
                  <RHFCheckbox
                    name="MarkFileAsMandatory"
                    label="Mark File As Mandatory"
                  />
                </Stack>

                <Stack display="flex" flexDirection="row-reverse">
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="info"
                    loading={isloaded}
                    disabled={isSubmitting}
                    onClick={() => {
                      if (!errors) {
                        onSubmit;
                      }
                    }}>
                    Save
                  </LoadingButton>
                </Stack>
              </Stack>
            </form>
          ))}
        {activeTab === 1 && (
          <NewFilesLockProperties
            uiFields={uiFields}
            Moduledata={task?.data ? task.data : undefined}
            insertdata={insertedId}
            tableNameEntity="ModuleLinkSubList"
          />
        )}
        {activeTab === 2 && (
          <Resources
            uiFields={uiFields}
            Moduledata={task?.data ? task.data : undefined}
            insertdata={insertedId}
            tableNameEntity="ContentModuleResourseLinks"
          />
        )}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert
            onClose={handleSnackbarClose}
            severity="warning"
            sx={{width: '40vw'}}>
            Basic information data must be added before accessing other tabs.
          </Alert>
        </Snackbar>
        <Snackbar
          open={saved}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{width: '40vw'}}>
            Basic Info is Saved
          </Alert>
        </Snackbar>
      </Drawer>
    </FormProvider>
  );
}
